import { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import AOS from "aos";
import { skills } from "../../data/constants";

import "./skills.css";
import SpotlightCard from "../ReusableComponents/SpotlightCard";

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const Section = styled.section`
  width: 100%;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 42px;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.text_primary};
  font-weight: 600;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const ScrollWrapper = styled.div`
  width: 100%;
  max-width: 1100px;
  padding: 0 40px;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    padding: 0 20px;
  }

  /* Fade edges */
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 80px;
    height: 100%;
    pointer-events: none;
    z-index: 10; /* force above skills */
  }

  &::before {
    left: 0;
    background: linear-gradient(
      to right,
      ${({ theme }) => theme.bg} 0%,
      transparent 100%
    );
  }

  &::after {
    right: 0;
    background: linear-gradient(
      to left,
      ${({ theme }) => theme.bg} 0%,
      transparent 100%
    );
  }
`;

const Track = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  animation: ${scroll} 10s linear infinite;
`;

const TrackInner = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;
  padding-right: 20px;
`;

const SkillPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text_primary};
  border: 1px solid rgba(255, 255, 255, 0.04);
  font-weight: 500;
  white-space: nowrap;

  img {
    width: 20px;
    height: 20px;
    display: block;
  }

  @media (max-width: 500px) {
    padding: 6px 10px;
    font-size: 14px;
  }
`;

const Skills = () => {
  useEffect(() => {
    AOS.init();
  }, []);

  const allSkills = skills.flatMap((s) => s.skills);

  return (
    <Section id="skills">
      <Title>SKILLS</Title>
      <div
        className="cards-container"
        style={{
          display: "flex",
          gap: "2rem",
          width: "100%",
          maxWidth: "1180px",
          padding: "0 40px",
          marginBottom: "2rem",
        }}
      >
        <SpotlightCard
          className="custom-spotlight-card"
          spotlightColor="rgba(255, 255, 255, 0.25)"
          style={{ flex: 1 }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>
              <img
                src="React.svg"
                alt="React"
                style={{ width: "40px", height: "40px" }}
              />
            </span>
            <h3
              style={{
                color: "#fff",
                marginBottom: "1rem",
                fontSize: "1.5rem",
              }}
            >
              Frontend Development
            </h3>
            <p style={{ color: "#ccc", fontSize: "1rem" }}>
              Specialized in creating responsive and interactive web
              applications using modern JavaScript frameworks and libraries.
            </p>
          </div>
        </SpotlightCard>

        <SpotlightCard
          className="custom-spotlight-card"
          spotlightColor="rgba(0, 229, 255, 0.2)"
          style={{ flex: 1 }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>
              <img
                src="SharePoint.svg"
                alt="SharePoint"
                style={{ width: "40px", height: "40px" }}
              />
            </span>
            <h3
              style={{
                color: "#fff",
                marginBottom: "1rem",
                fontSize: "1.5rem",
              }}
            >
              SharePoint Development
            </h3>
            <p style={{ color: "#ccc", fontSize: "1rem" }}>
              Expert in building custom SharePoint solutions and integrations
              using SPFx and modern web technologies.
            </p>
          </div>
        </SpotlightCard>
        <SpotlightCard
          className="custom-spotlight-card"
          spotlightColor="rgba(255, 111, 0, 0.2)"
          style={{ flex: 1 }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>
              <img
                src="Responsive.svg"
                alt="SharePoint"
                style={{ width: "40px", height: "40px" }}
              />
            </span>
            <h3
              style={{
                color: "#fff",
                marginBottom: "1rem",
                fontSize: "1.5rem",
              }}
            >
              Responsive Design
            </h3>
            <p style={{ color: "#ccc", fontSize: "1rem" }}>
              Expert in Making Browser Responsive, Mobile Responsive & Iphone
              Mobile Responsive.
            </p>
          </div>
        </SpotlightCard>
      </div>
      <ScrollWrapper>
        <Track>
          <TrackInner>
            {allSkills.map((item, i) => (
              <SkillPill key={`${item.name}-${i}`}>
                <img src={item.image} alt={item.name} />
                {item.name}
              </SkillPill>
            ))}
          </TrackInner>

          <TrackInner aria-hidden="true">
            {allSkills.map((item, i) => (
              <SkillPill key={`${item.name}-dup-${i}`}>
                <img src={item.image} alt={item.name} />
                {item.name}
              </SkillPill>
            ))}
          </TrackInner>
        </Track>
      </ScrollWrapper>
    </Section>
  );
};

export default Skills;
